/**
 * BatchImportModal
 *
 * Multi-booking → Drive batch import. User selects N photos in PhotoGrid,
 * this modal:
 *   1. Auto-detects each booking's `compressed/` source folder in parallel
 *   2. Surfaces a summary table — bookings without a compressed folder are
 *      flagged and excluded by default (user can fix manually one-by-one
 *      via the per-booking ImportToDriveModal afterwards)
 *   3. User picks ONE parent destination folder
 *   4. Fires N parallel `/api/import-drive` jobs — server creates a
 *      subfolder named after each booking's `chatName` under the parent
 *
 * Backend takes no changes: each job uses the booking's own `photoId`
 * as the KV key, so jobs don't collide.
 */

import React, { useCallback, useEffect, useState } from "react";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import {
  useGoogleDrive,
  extractFolderIdFromUrl,
  getFolder,
  getFolderWithParentName,
  listSubfolders,
  type DriveFolder,
} from "../hooks/useGoogleDrive.js";
import { startImportDrive } from "../lib/api.js";

const LAST_FOLDER_KEY = "kra_last_drive_folder";
const COMPRESSED_RE = /compres/i;
const EXCLUDE_RE = /(uncompres|original|full|raw)/i;

// ── Types ─────────────────────────────────────────────────────────────────────

type Step =
  | "auth-checking"
  | "auth"
  | "detecting"
  | "summary"
  | "folder-browser"
  | "queueing"
  | "done"
  | "error";

type DetectStatus = "ok" | "no-compressed" | "invalid-link";

interface BookingState {
  photo: PhotoRecord;
  status: DetectStatus;
  sourceFolderId: string | null;
  sourceFolderName: string;
  /** Whether this booking will be queued. Defaults true if status === "ok". */
  included: boolean;
}

interface Props {
  photos: PhotoRecord[];
  onClose: () => void;
  onQueued: (queuedIds: string[]) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function BatchImportModal({ photos, onClose, onQueued }: Props) {
  const { accessToken: existingToken, requestToken, requestTokenSilent } = useGoogleDrive();

  const [step, setStep] = useState<Step>(existingToken ? "detecting" : "auth-checking");
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(existingToken);
  const [bookings, setBookings] = useState<BookingState[]>([]);

  // Destination folder
  const [destFolderId, setDestFolderId] = useState<string | null>(null);
  const [destFolderName, setDestFolderName] = useState<string>("");

  // In-app folder browser
  const [browserStack, setBrowserStack] = useState<DriveFolder[]>([]);
  const [browserFolders, setBrowserFolders] = useState<DriveFolder[]>([]);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserInited, setBrowserInited] = useState(false);

  // Queueing progress
  const [queuedCount, setQueuedCount] = useState(0);

  // ── Step 1: Auth ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (existingToken) return;
    let cancelled = false;
    requestTokenSilent().then((t) => {
      if (cancelled) return;
      if (t) {
        setToken(t);
        setStep("detecting");
      } else {
        setStep("auth");
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = useCallback(async () => {
    setError(null);
    try {
      const t = await requestToken();
      setToken(t);
      setStep("detecting");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lỗi đăng nhập Google");
    }
  }, [requestToken]);

  // ── Step 2: Detect compressed/ for each booking in parallel ────────────────
  useEffect(() => {
    if (step !== "detecting" || !token) return;

    const detectOne = async (photo: PhotoRecord): Promise<BookingState> => {
      const rootId = extractFolderIdFromUrl(photo.driveLink);
      if (!rootId) {
        return { photo, status: "invalid-link", sourceFolderId: null, sourceFolderName: "", included: false };
      }
      try {
        const subs = await listSubfolders(rootId, token);
        const compressed = subs.find((f) => COMPRESSED_RE.test(f.name) && !EXCLUDE_RE.test(f.name));
        if (compressed) {
          return { photo, status: "ok", sourceFolderId: compressed.id, sourceFolderName: compressed.name, included: true };
        }
        if (subs.length === 0) {
          // No subfolders → treat the root as the source (mirrors single-import behavior)
          const root = await getFolder(rootId, token);
          return { photo, status: "ok", sourceFolderId: rootId, sourceFolderName: root.name, included: true };
        }
        return { photo, status: "no-compressed", sourceFolderId: null, sourceFolderName: "", included: false };
      } catch {
        return { photo, status: "invalid-link", sourceFolderId: null, sourceFolderName: "", included: false };
      }
    };

    void (async () => {
      try {
        const results = await Promise.all(photos.map(detectOne));
        setBookings(results);
        setStep("summary");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi khi quét thư mục");
        setStep("error");
      }
    })();
  }, [step, token, photos]);

  const toggleInclude = useCallback((id: string) => {
    setBookings((prev) =>
      prev.map((b) => (b.photo.id === id && b.status === "ok" ? { ...b, included: !b.included } : b))
    );
  }, []);

  const okCount = bookings.filter((b) => b.status === "ok").length;
  const includedCount = bookings.filter((b) => b.included).length;
  const skippedCount = bookings.length - okCount;

  // ── Step 3: Folder browser ─────────────────────────────────────────────────
  useEffect(() => {
    if (step !== "folder-browser" || !token || browserInited) return;
    setBrowserInited(true);
    void (async () => {
      setBrowserLoading(true);
      const savedId = localStorage.getItem(LAST_FOLDER_KEY);
      let startId = "root";
      let startName = "My Drive";
      if (savedId) {
        try {
          const info = await getFolder(savedId, token);
          startId = info.id;
          startName = info.name;
        } catch { /* fall back */ }
      }
      try {
        const folders = await listSubfolders(startId, token);
        setBrowserStack([{ id: startId, name: startName }]);
        setBrowserFolders(folders);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      setBrowserLoading(false);
    })();
  }, [step, token, browserInited]);

  const handleBrowserEnter = useCallback(async (folder: DriveFolder) => {
    if (!token) return;
    setBrowserLoading(true);
    try {
      const folders = await listSubfolders(folder.id, token);
      setBrowserStack((prev) => [...prev, folder]);
      setBrowserFolders(folders);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lỗi tải thư mục");
    }
    setBrowserLoading(false);
  }, [token]);

  /**
   * Go up one level. Two paths:
   *  - We descended via chevrons this session → just pop the stack (no API).
   *  - We started deep at the saved folder (stack length 1) → resolve the
   *    real parent from Drive so the admin isn't stuck at the start folder.
   */
  const handleBrowserBack = useCallback(async () => {
    if (!token) return;

    // Fast path: in-session history exists → pop locally.
    if (browserStack.length > 1) {
      const newStack = browserStack.slice(0, -1);
      const parent = newStack[newStack.length - 1]!;
      setBrowserLoading(true);
      try {
        const folders = await listSubfolders(parent.id, token);
        setBrowserStack(newStack);
        setBrowserFolders(folders);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Lỗi tải thư mục");
      }
      setBrowserLoading(false);
      return;
    }

    // No history → fetch the actual parent of the current folder.
    const current = browserStack[0];
    if (!current || current.id === "root") return;
    setBrowserLoading(true);
    try {
      const info = await getFolderWithParentName(current.id, token);
      if (!info.parentId) {
        // Already at Drive root — nothing above.
        setBrowserLoading(false);
        return;
      }
      const parent: DriveFolder = { id: info.parentId, name: info.parentName ?? "Thư mục mẹ" };
      const folders = await listSubfolders(parent.id, token);
      setBrowserStack([parent]);
      setBrowserFolders(folders);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lỗi lên thư mục mẹ");
    }
    setBrowserLoading(false);
  }, [token, browserStack]);

  const handleBrowserPick = useCallback((folder: DriveFolder) => {
    const parent = browserStack[browserStack.length - 1];
    if (parent) localStorage.setItem(LAST_FOLDER_KEY, parent.id);
    setDestFolderId(folder.id);
    setDestFolderName(folder.name);
  }, [browserStack]);

  const handleBrowserSelectCurrent = useCallback(() => {
    const current = browserStack[browserStack.length - 1];
    if (!current) return;
    if (browserStack.length >= 2) {
      localStorage.setItem(LAST_FOLDER_KEY, browserStack[browserStack.length - 2]!.id);
    } else {
      localStorage.setItem(LAST_FOLDER_KEY, current.id);
    }
    setDestFolderId(current.id);
    setDestFolderName(current.name);
  }, [browserStack]);

  // ── Step 4: Queue all included jobs ────────────────────────────────────────
  const handleQueueAll = useCallback(async () => {
    if (!token || !destFolderId) return;
    setStep("queueing");
    setError(null);
    setQueuedCount(0);

    const toQueue = bookings.filter((b) => b.included && b.sourceFolderId);
    const queuedIds: string[] = [];
    let failed = 0;

    // Fire in parallel but track per-job result so partial failures surface.
    await Promise.all(
      toQueue.map(async (b) => {
        try {
          await startImportDrive({
            oauthToken: token,
            sourceFolderId: b.sourceFolderId!,
            destFolderId,
            photoId: b.photo.id,
            chatName: b.photo.chatName,
            folderName: b.photo.chatName,
          });
          queuedIds.push(b.photo.id);
          setQueuedCount((c) => c + 1);
        } catch {
          failed += 1;
        }
      })
    );

    if (failed > 0) {
      setError(`${queuedIds.length}/${toQueue.length} bộ đã queue. ${failed} bộ lỗi — thử lại sau.`);
    }
    onQueued(queuedIds);
    setStep("done");
  }, [token, destFolderId, bookings, onQueued]);

  // ── Render ──────────────────────────────────────────────────────────────────
  const currentBrowserFolder = browserStack[browserStack.length - 1];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-md rounded-[28px] bg-[#fbfaf6] p-6 shadow-2xl">
        {/* Header */}
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-3">
              Batch Import to Drive
            </p>
            <h3 className="mt-1 text-lg font-semibold text-ink-0 leading-tight">
              {photos.length} bộ đã chọn
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Đóng"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface-2 hover:text-ink-0 transition-colors"
          >
            ×
          </button>
        </div>

        {/* Auth: silent refresh */}
        {step === "auth-checking" && (
          <div className="flex items-center justify-center gap-3 py-6">
            <Spinner />
            <span className="text-sm text-ink-2">Đang kiểm tra phiên Google…</span>
          </div>
        )}

        {/* Auth: manual sign-in */}
        {step === "auth" && (
          <div className="space-y-4">
            <p className="text-sm text-ink-2">
              Phiên Google đã hết hạn. Đăng nhập lại để batch import.
            </p>
            <button
              onClick={() => { void handleAuth(); }}
              className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-80"
            >
              Đăng nhập Google
            </button>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        )}

        {/* Detecting */}
        {step === "detecting" && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Spinner />
            <p className="text-sm text-ink-2">Đang quét compressed/ cho {photos.length} bộ…</p>
          </div>
        )}

        {/* Summary */}
        {step === "summary" && (
          <div className="space-y-4">
            <div className="rounded-[14px] bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 leading-relaxed">
              <strong>{includedCount}/{bookings.length}</strong> bộ sẽ được import.
              {skippedCount > 0 && (
                <> {skippedCount} bộ không có thư mục <code>compressed/</code> — bỏ qua, có thể import thủ công sau.</>
              )}
            </div>

            <div className="rounded-[18px] border border-surface-2 overflow-hidden bg-white">
              <div className="max-h-72 overflow-y-auto divide-y divide-surface-2">
                {bookings.map((b) => (
                  <BookingRow
                    key={b.photo.id}
                    booking={b}
                    onToggle={() => toggleInclude(b.photo.id)}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={() => setStep("folder-browser")}
              disabled={includedCount === 0}
              className="w-full rounded-[18px] bg-ink-0 py-3 text-sm font-semibold text-white hover:opacity-80 disabled:opacity-30 transition-opacity"
            >
              Chọn thư mục đích → ({includedCount} bộ)
            </button>
          </div>
        )}

        {/* Folder browser */}
        {step === "folder-browser" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 min-w-0">
              {currentBrowserFolder && currentBrowserFolder.id !== "root" && (
                <button
                  onClick={() => { void handleBrowserBack(); }}
                  disabled={browserLoading}
                  className="flex-shrink-0 text-sm text-ink-2 hover:text-ink-0 disabled:opacity-40 transition-colors"
                >
                  ‹ Lên thư mục mẹ
                </button>
              )}
              <span className="text-sm font-semibold text-ink-0 truncate">
                📁 {currentBrowserFolder?.name ?? "My Drive"}
              </span>
            </div>

            <button
              onClick={handleBrowserSelectCurrent}
              disabled={browserLoading || !currentBrowserFolder}
              className="w-full rounded-[18px] border-2 border-ink-0 bg-white py-3 text-sm font-semibold text-ink-0 hover:bg-surface-1 disabled:opacity-30 transition-colors"
            >
              Chọn “{currentBrowserFolder?.name ?? "thư mục này"}” làm parent
            </button>
            <p className="text-[11px] text-ink-3 text-center -mt-1">
              Tap tên folder = chọn làm parent · “Mở ›” = xem các thư mục bên trong
            </p>

            <div className="rounded-[18px] border border-surface-2 overflow-hidden">
              {browserLoading ? (
                <div className="flex justify-center py-6"><Spinner /></div>
              ) : browserFolders.length === 0 ? (
                <p className="text-sm text-ink-3 text-center py-5">Không có thư mục con</p>
              ) : (
                <div className="max-h-48 overflow-y-auto divide-y divide-surface-2">
                  {browserFolders.map((f) => (
                    <div key={f.id} className="flex items-center w-full text-sm text-ink-0">
                      <button
                        onClick={() => handleBrowserPick(f)}
                        className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3 text-left hover:bg-surface-1 active:bg-surface-2 transition-colors"
                      >
                        <span className="text-base">📁</span>
                        <span className="flex-1 truncate">{f.name}</span>
                      </button>
                      <button
                        onClick={() => { void handleBrowserEnter(f); }}
                        aria-label={`Mở ${f.name}`}
                        className="flex h-full items-center gap-1 px-3.5 py-3 text-xs font-medium text-ink-2 hover:bg-surface-1 active:bg-surface-2 transition-colors border-l border-surface-2"
                      >
                        Mở
                        <span className="text-base leading-none">›</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {destFolderId && (
              <div className="rounded-[14px] bg-green-50 border border-green-200 px-4 py-3 text-xs text-green-800 space-y-2">
                <div>
                  Parent: <strong>{destFolderName}</strong>. Sẽ tạo {includedCount} subfolder bên trong.
                </div>
                <button
                  onClick={() => { void handleQueueAll(); }}
                  className="w-full rounded-[14px] bg-ink-0 py-2.5 text-sm font-semibold text-white hover:opacity-80 transition-opacity"
                >
                  Bắt đầu import {includedCount} bộ →
                </button>
              </div>
            )}

            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        )}

        {/* Queueing progress */}
        {step === "queueing" && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Spinner />
            <p className="text-sm text-ink-2">
              Đang queue {queuedCount}/{includedCount}…
            </p>
          </div>
        )}

        {/* Done */}
        {step === "done" && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-[18px] border border-green-200 bg-green-50 px-4 py-4">
              <span className="text-2xl mt-0.5">🚀</span>
              <div>
                <p className="text-sm font-semibold text-green-800">{queuedCount} bộ đã queue!</p>
                <p className="text-xs text-green-700 mt-1 leading-relaxed">
                  Server đang copy ảnh nền. Discord sẽ báo từng bộ khi xong (mỗi bộ ~60-120s).
                </p>
              </div>
            </div>
            {error && (
              <div className="rounded-[14px] bg-red-50 border border-red-200 px-4 py-3 text-xs text-red-700">
                {error}
              </div>
            )}
            <button
              onClick={onClose}
              className="w-full rounded-[18px] border border-surface-2 bg-white py-3 text-sm font-medium text-ink-1 hover:bg-surface-1 transition-colors"
            >
              Đóng
            </button>
          </div>
        )}

        {/* Error */}
        {step === "error" && (
          <div className="space-y-4">
            <div className="rounded-[18px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error ?? "Đã xảy ra lỗi"}
            </div>
            <button
              onClick={() => { setError(null); setStep("auth"); }}
              className="w-full rounded-[18px] border border-surface-2 bg-white py-3 text-sm font-medium text-ink-1 hover:bg-surface-1 transition-colors"
            >
              Thử lại
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Booking row in summary ────────────────────────────────────────────────────

function BookingRow({ booking, onToggle }: { booking: BookingState; onToggle: () => void }) {
  const { photo, status, sourceFolderName, included } = booking;
  const isOk = status === "ok";
  const label = isOk
    ? `compressed: ${sourceFolderName}`
    : status === "no-compressed"
      ? "Không có thư mục compressed/"
      : "Link Drive không hợp lệ";

  return (
    <button
      type="button"
      onClick={isOk ? onToggle : undefined}
      disabled={!isOk}
      className={
        "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors " +
        (isOk ? "hover:bg-surface-1 active:bg-surface-2 cursor-pointer" : "opacity-60 cursor-default")
      }
    >
      <div
        className={
          "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 " +
          (included ? "bg-[#B76E6E] border-[#B76E6E]" : "bg-white border-surface-2")
        }
      >
        {included && (
          <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink-0 truncate">{photo.chatName}</p>
        <p className={"text-[11px] truncate " + (isOk ? "text-ink-3" : "text-amber-700")}>
          {isOk ? "✓ " : "⚠ "}{label}
        </p>
      </div>
    </button>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg className="animate-spin h-6 w-6 text-ink-2" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}
